import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

const POSTED_FLAG = 'be-newsletter-posted';
const HASH = '#be-newsletter';
const TRIGGER_SELECTOR = `a[href$="${HASH}"]`;

/**
 * The newsletter subscribe overlay: a hidden, pre-rendered dialog available on
 * every page (sections/newsletter-signup.liquid, rendered from theme.liquid).
 *
 * Any anchor whose href ends in `#be-newsletter` opens it, so merchants can
 * trigger it with a plain navigation menu item — no theme markup required.
 * (The menu snippets add aria-haspopup="dialog" to those links server-side.)
 * The menu overlay the click came from (mega menu dialog or header drawer)
 * is closed first so it isn't left open behind the panel; focus then returns
 * to that menu's own trigger when this overlay closes, because the clicked
 * link is unfocusable inside the closed menu and the browser's native focus
 * restore would drop focus to <body>.
 *
 * A `#be-newsletter` URL hash on load also opens it, making e.g.
 * /#be-newsletter a shareable deep link (the overlay is rendered on every
 * page). The hash is stripped once consumed so a refresh after closing
 * doesn't reopen it.
 *
 * The subscribe form posts and reloads the page; a sessionStorage flag set on
 * submit reopens the overlay after the reload so the success or error message
 * is visible. The flag is only consumed on a page that actually rendered a
 * form result: Shopify may answer the post with the /challenge captcha
 * interstitial first, and the flag has to survive it so the real result page
 * still reopens the overlay. The result markup is revealed here rather than
 * server-side because Shopify's posted state is global — a post from any
 * other customer form (e.g. a footer email signup) renders the same success
 * state into this section.
 */
class NewsletterOverlayComponent extends DialogComponent {
  /** @type {HTMLElement | null} */
  #returnFocus = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', this.#handleTriggerClick);
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    const openedForResult = this.#showPostResult();
    this.#openFromHash(openedForResult);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleTriggerClick);
  }

  /**
   * Opens the overlay when the page loads with the `#be-newsletter` hash.
   *
   * @param {boolean} alreadyOpened - Whether the post-result path already
   *   requested the dialog open this load (showModal() runs a frame later,
   *   so a second showDialog() call would slip past its `open` guard).
   */
  #openFromHash(alreadyOpened) {
    if (window.location.hash !== HASH) return;

    history.replaceState(null, '', window.location.pathname + window.location.search);
    if (!alreadyOpened) this.showDialog();
  }

  /**
   * Reveals and announces this form's post result, if the reload carried one.
   *
   * @returns {boolean} Whether the overlay was opened to show a result.
   */
  #showPostResult() {
    const message = this.querySelector('.be-newsletter__message');

    let posted = false;
    try {
      posted = sessionStorage.getItem(POSTED_FLAG) != null;
    } catch {
      // Storage can be blocked entirely; the overlay just won't reopen.
    }

    if (message instanceof HTMLElement && posted) {
      try {
        sessionStorage.removeItem(POSTED_FLAG);
      } catch {
        // Ignored: worst case the overlay reopens on one more result page.
      }

      message.hidden = false;

      if (message.id === 'be-newsletter-error') {
        // showModal()'s autofocus lands on the dialog itself; move focus to
        // the error so screen readers announce it.
        this.addEventListener(DialogOpenEvent.eventName, () => message.focus(), { once: true });
      }

      this.showDialog();
      return true;
    }

    if (message) {
      // Another customer form on the page posted; the shared result state
      // rendered into this section isn't ours to show.
      const email = this.querySelector('#be-newsletter-email');
      email?.removeAttribute('aria-invalid');
      email?.removeAttribute('aria-describedby');
    }

    return false;
  }

  /**
   * Opens the overlay in place of following a trigger link.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleTriggerClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest(TRIGGER_SELECTOR);

    if (!link || this.contains(link)) return;

    event.preventDefault();
    this.#returnFocus = this.#closeContainingMenu(link);
    this.showDialog();
  };

  /**
   * Closes the menu overlay a trigger link was clicked inside, if any.
   *
   * @param {Element} link - The clicked trigger link.
   * @returns {HTMLElement | null} The closed menu's own visible trigger, kept
   *   as the focus target for when this overlay closes.
   */
  #closeContainingMenu(link) {
    for (let element = link.parentElement; element; element = element.parentElement) {
      if (element instanceof DialogComponent) {
        element.closeDialog();

        if (!element.id) return null;
        const openers = document.querySelectorAll(`[aria-controls="${element.id}"]`);
        for (const opener of openers) {
          if (opener instanceof HTMLElement && opener.checkVisibility()) return opener;
        }
        return null;
      }

      if (element.localName === 'header-drawer') {
        /** @type {{ close?: () => void }} */ (element).close?.();

        const summary = element.querySelector('summary');
        return summary instanceof HTMLElement ? summary : null;
      }
    }

    return null;
  }

  #handleDialogClose = () => {
    const target = this.#returnFocus;
    this.#returnFocus = null;

    if (!target?.isConnected) return;

    // Only step in when the browser's own focus restore failed (the trigger
    // link sits inside a now-closed menu, so focus fell back to <body>).
    if (document.activeElement === document.body || document.activeElement === null) {
      target.focus();
    }
  };

  #handleSubmit = () => {
    try {
      sessionStorage.setItem(POSTED_FLAG, '1');
    } catch {
      // Storage blocked: the form still posts, the overlay won't reopen.
    }
  };
}

if (!customElements.get('be-newsletter-overlay-component')) {
  customElements.define('be-newsletter-overlay-component', NewsletterOverlayComponent);
}
